import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { type GetBillingOverviewResponse } from '@acs/shared';
import { Banner, Button, Icon, Skeleton, useToast } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { friendlyError } from '../../shared/api/errors.js';
import { billingErrorDetails } from '../../shared/api/billing.js';
import { billingError } from './billingText.js';
import { formatMoney } from './format.js';
import { useOpenBillingPortal, useStartCheckout } from './hooks.js';
import {
  cheapestPerMinute,
  ESTIMATE_VAT_PERCENT,
  estimatedCalls,
  estimateGross,
  initialOptionalPackage,
  initialPackage,
  MINUTES_PER_CALL,
  NO_PACKAGE,
  packageMinutes,
  perMinuteText,
  readSelectedPackage,
  writeSelectedPackage,
  type PackageView,
} from './planMath.js';
import { useBuyTopup, useCancelTopup, usePackageQuote, useSyncTopup } from './prepaidHooks.js';

type Overview = GetBillingOverviewResponse;

/**
 * The plan screen of the doc 57 offer: ONE monthly subscription and four
 * one-time minute packages (design/PLANS-SELECTION-WIDGET-REQUIREMENTS.md).
 *
 * Two flows share the package picker and the summary:
 * - `SubscribeAndChoose`: a new subscriber picks a package, then pays the
 *   subscription first (Stripe Checkout). The package waits in session storage
 *   for the second payment.
 * - `BuyMinutes`: a subscribed owner buys a package. The price shown on the
 *   button is the server's exact quote, never the estimate.
 *
 * Every price comes from the server (Stripe price objects / quotes). The only
 * local arithmetic is the labelled VAT estimate shown before a quote can exist.
 */

/**
 * Remembered selection, kept in sync with storage for this business. With
 * `optional`, "no additional package" (null) is a real choice and the default.
 */
export function usePackageSelection(businessId: string, packages: readonly PackageView[], optional = false) {
  const resolve = () => {
    const remembered = readSelectedPackage(businessId);
    return optional ? initialOptionalPackage(packages, remembered) : initialPackage(packages, remembered);
  };
  const [selected, setSelected] = useState<string | null>(resolve);
  // A business switch or a refreshed catalogue: re-resolve rather than keep a
  // package the new list does not have.
  useEffect(() => {
    setSelected((cur) => (cur && packages.some((p) => p.packageId === cur) ? cur : resolve()));
  }, [businessId, packages, optional]); // eslint-disable-line react-hooks/exhaustive-deps
  const choose = (id: string | null) => {
    setSelected(id);
    writeSelectedPackage(businessId, id ?? NO_PACKAGE);
  };
  return [selected, choose] as const;
}

// ── Package radio group ─────────────────────────────────────────────────────

export function PackagePicker({
  packages,
  selected,
  onSelect,
  labelledBy,
  disabled,
  none,
  plus,
}: {
  packages: readonly PackageView[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  labelledBy: string;
  disabled?: boolean;
  /** An explicit "no additional package" radio, first (doc 59 N07). */
  none?: { label: string; hint: string };
  /** "+300 min": minutes added on top of the balance (Settings › Billing). */
  plus?: boolean;
}) {
  const { t, tag } = useI18n();
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const cheapest = cheapestPerMinute(packages);
  if (none) return <OptionalPicker packages={packages} selected={selected} onSelect={onSelect} labelledBy={labelledBy} none={none} />;
  const selectable = packages.map((p) => p.unitAmountMinor !== null && !disabled);
  // Roving focus: exactly one radio is tabbable — the checked one, or the
  // first selectable when nothing is checked.
  const checkedIndex = packages.findIndex((p) => p.packageId === selected);
  const tabbable = checkedIndex >= 0 && selectable[checkedIndex] ? checkedIndex : selectable.indexOf(true);

  const move = (from: number, step: number) => {
    for (let i = 1; i <= packages.length; i += 1) {
      const next = (from + step * i + packages.length * i) % packages.length;
      if (selectable[next]) {
        onSelect(packages[next]!.packageId);
        refs.current[next]?.focus();
        return;
      }
    }
  };
  const onKey = (e: KeyboardEvent, i: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      move(i, 1);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      move(i, -1);
    } else if ((e.key === ' ' || e.key === 'Enter') && selectable[i]) {
      e.preventDefault();
      onSelect(packages[i]!.packageId);
    }
  };

  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="plan-packages">
      {packages.map((p, i) => {
        const checked = p.packageId === selected;
        const minutes = packageMinutes(p);
        const price = p.unitAmountMinor !== null ? formatMoney(p.unitAmountMinor, p.currency, tag) : null;
        return (
          <div
            key={p.packageId}
            ref={(el) => {
              refs.current[i] = el;
            }}
            role="radio"
            aria-checked={checked}
            aria-disabled={!selectable[i] || undefined}
            aria-label={price ? t('plan.package.aria', { minutes, amount: price }) : t('plan.package.minutes', { minutes })}
            tabIndex={i === tabbable && selectable[i] ? 0 : -1}
            className={`plan-package${checked ? ' plan-package--on' : ''}${selectable[i] ? '' : ' plan-package--off'}`}
            onClick={() => selectable[i] && onSelect(p.packageId)}
            onKeyDown={(e) => onKey(e, i)}
          >
            <PackageRowBody p={p} lowest={cheapest === p.packageId} plus={plus} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The same radio group with "no additional package" as its first, default
 * option. Arrow keys move over every option; Space/Enter selects.
 */
function OptionalPicker({
  packages,
  selected,
  onSelect,
  labelledBy,
  none,
}: {
  packages: readonly PackageView[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  labelledBy: string;
  none: { label: string; hint: string };
}) {
  const { t, tag } = useI18n();
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const cheapest = cheapestPerMinute(packages);
  const items: { id: string | null; selectable: boolean }[] = [
    { id: null, selectable: true },
    ...packages.map((p) => ({ id: p.packageId, selectable: p.unitAmountMinor !== null })),
  ];
  const checkedIndex = items.findIndex((i) => i.id === selected);
  const tabbable = checkedIndex >= 0 ? checkedIndex : 0;
  const move = (from: number, step: number) => {
    for (let k = 1; k <= items.length; k += 1) {
      const next = (from + step * k + items.length * k) % items.length;
      if (items[next]!.selectable) {
        onSelect(items[next]!.id);
        refs.current[next]?.focus();
        return;
      }
    }
  };
  const onKey = (e: KeyboardEvent, i: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      move(i, 1);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      move(i, -1);
    } else if ((e.key === ' ' || e.key === 'Enter') && items[i]!.selectable) {
      e.preventDefault();
      onSelect(items[i]!.id);
    }
  };
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="plan-packages">
      {items.map((item, i) => {
        const checked = item.id === selected;
        const p = item.id ? packages.find((x) => x.packageId === item.id)! : null;
        const minutes = p ? packageMinutes(p) : 0;
        const price = p && p.unitAmountMinor !== null ? formatMoney(p.unitAmountMinor, p.currency, tag) : null;
        return (
          <div
            key={item.id ?? NO_PACKAGE}
            ref={(el) => {
              refs.current[i] = el;
            }}
            role="radio"
            aria-checked={checked}
            aria-disabled={!item.selectable || undefined}
            aria-label={p ? (price ? t('plan.package.aria', { minutes, amount: price }) : t('plan.package.minutes', { minutes })) : none.label}
            tabIndex={i === tabbable ? 0 : -1}
            className={`plan-package${checked ? ' plan-package--on' : ''}${item.selectable ? '' : ' plan-package--off'}`}
            onClick={() => item.selectable && onSelect(item.id)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {p ? <PackageRowBody p={p} lowest={cheapest === p.packageId} /> : <PackageRowBody none={none} />}
          </div>
        );
      })}
    </div>
  );
}

/**
 * One radio's content (design v4): the minutes large, a "≈ N calls*" pill,
 * the per-minute price under it, and on the right the net price with
 * "+ VAT" — plus the one factual tag, "Lowest per minute". The "no extra
 * minutes" option shares the layout so the list reads as one set.
 */
function PackageRowBody(props: { p: PackageView; lowest: boolean; plus?: boolean } | { none: { label: string; hint: string } }) {
  const { t, tag } = useI18n();
  const dot = <span className="plan-package__dot" aria-hidden="true" />;
  if ('none' in props) {
    return (
      <>
        {dot}
        <span className="plan-package__body">
          <span className="plan-package__top">
            <span className="plan-package__minutes">{props.none.label}</span>
          </span>
          <span className="plan-package__meta">{props.none.hint}</span>
        </span>
      </>
    );
  }
  const { p, lowest, plus } = props;
  const minutes = packageMinutes(p);
  const perMinute = perMinuteText(p, tag);
  return (
    <>
      {dot}
      <span className="plan-package__body">
        <span className="plan-package__top">
          <span className="plan-package__minutes">{t(plus ? 'plan.package.minutesPlus' : 'plan.package.minutes', { minutes })}</span>
          <span className="plan-calls">
            <Icon name="phone" size={13} strokeWidth={2.4} />
            {t('plan.package.calls', { calls: estimatedCalls(minutes) })}
          </span>
        </span>
        <span className="plan-package__meta">{perMinute ? t('plan.package.perMinute', { amount: perMinute }) : t('plan.package.oneTime')}</span>
      </span>
      <span className="plan-package__price">
        {lowest && <span className="plan-package__tag">{t('plan.package.lowestTag')}</span>}
        {p.unitAmountMinor !== null ? (
          <>
            <span className="plan-package__amount">{formatMoney(p.unitAmountMinor, p.currency, tag)}</span>
            <span className="plan-package__vat">{t('plan.package.plusVat')}</span>
          </>
        ) : (
          <span className="plan-package__vat">{t('plan.package.unavailable')}</span>
        )}
      </span>
    </>
  );
}

/** The footnote every "≈ N calls*" points at. */
export function CallsFootnote() {
  const { t } = useI18n();
  return <p className="plan-muted plan-footnote">{t('plan.package.callsNote', { minutes: MINUTES_PER_CALL })}</p>;
}

// ── Small pieces ────────────────────────────────────────────────────────────

function Line({ label, value, strong }: { label: ReactNode; value: ReactNode; strong?: boolean }) {
  return (
    <div className={`plan-line${strong ? ' plan-line--strong' : ''}`}>
      <span>{label}</span>
      <span className="plan-num">{value}</span>
    </div>
  );
}

/** The net / VAT / total lines while the quote is being fetched. */
function QuoteSkeleton() {
  return (
    <>
      <Line label={<Skeleton height={16} width={140} />} value={<Skeleton height={16} width={72} />} />
      <Line label={<Skeleton height={16} width={90} />} value={<Skeleton height={16} width={56} />} />
      <Line strong label={<Skeleton height={20} width={70} />} value={<Skeleton height={32} width={120} />} />
    </>
  );
}

/** A summary line's label with a muted second line under it. */
function LineLabel({ main, sub }: { main: string; sub: string }) {
  return (
    <span className="plan-line__label">
      <span>{main}</span>
      <span className="plan-line__sub">{sub}</span>
    </span>
  );
}

const FEATURES = ['receptionist', 'booking', 'sms', 'calendar', 'clients', 'staff', 'history', 'support'] as const;

/**
 * What the monthly fee pays for — the same list for every customer. With an
 * included-minutes offer (design v4) the minutes are the headline: a large
 * block on the brand colour, with a rough call count, ahead of the features.
 */
export function SubscriptionBlock({ ov }: { ov: Overview }) {
  const { t, tag } = useI18n();
  const net = ov.platform.unitAmountMinor;
  const includedMinutes = Math.floor((ov.included?.perPeriodSeconds ?? 0) / 60);
  return (
    <section aria-labelledby="plan-sub-h" className={`plan-sub${includedMinutes > 0 ? ' plan-sub--card' : ''}`}>
      <div className="plan-sub__head">
        <h2 id="plan-sub-h" className="plan-h2">{t('plan.sub.titleShort')}</h2>
        {net !== null && (
          <span className="plan-sub__price">
            {formatMoney(net, ov.currency, tag)}
            <span className="plan-sub__vat"> {t('plan.sub.perMonthVat')}</span>
          </span>
        )}
      </div>
      {includedMinutes > 0 && (
        <div className="plan-included">
          <span className="plan-included__num" aria-hidden="true">{includedMinutes}</span>
          <span className="plan-included__text">
            <span className="visually-hidden">{includedMinutes} </span>
            <span>{t('plan.sub.highlight.title')}</span>
            <span>{t('plan.sub.highlight.sub', { calls: estimatedCalls(includedMinutes) })}</span>
          </span>
        </div>
      )}
      <ul className="plan-features">
        {FEATURES.map((f) => (
          <li key={f}>
            <span aria-hidden="true" className="plan-features__tick">✓</span>
            {t(`plan.sub.feature.${f}` as TranslationKey)}
          </li>
        ))}
      </ul>
      <p className="plan-muted plan-sub__note">
        {includedMinutes > 0 ? t('plan.sub.unitIncluded') : `${t('plan.sub.unit')} ${t('plan.sub.noMinutes')}`}
      </p>
    </section>
  );
}

export function BillingFaq({ includedMinutes = 0 }: { includedMinutes?: number }) {
  const { t } = useI18n();
  return (
    <details className="plan-faq">
      <summary>{t('plan.faq.title')}</summary>
      <ul>
        <li>{includedMinutes > 0 ? t('plan.faq.subscriptionIncluded', { minutes: includedMinutes }) : t('plan.faq.subscription')}</li>
        {includedMinutes > 0 && <li>{t('plan.faq.includedExpiry')}</li>}
        <li>{t('plan.faq.minutes')}</li>
        <li>{t('plan.faq.auto')}</li>
        <li>{t('plan.faq.failure')}</li>
      </ul>
    </details>
  );
}

// ── New subscriber: pick, then pay the subscription ─────────────────────────

export function SubscribeAndChoose({ business, ov }: { business: BusinessDoc; ov: Overview }) {
  const { t, tag } = useI18n();
  const checkout = useStartCheckout(business.id);
  // With included minutes, a package is an optional add-on (doc 59 N07).
  const includedMinutes = Math.floor((ov.included?.perPeriodSeconds ?? 0) / 60);
  const optional = includedMinutes > 0;
  const [selected, choose] = usePackageSelection(business.id, ov.packages, optional);
  const vat = ESTIMATE_VAT_PERCENT[business.country] ?? null;
  const pkg = ov.packages.find((p) => p.packageId === selected) ?? null;
  const subNet = ov.platform.unitAmountMinor;
  const minNet = pkg?.unitAmountMinor ?? null;
  const money = (m: number) => formatMoney(m, ov.currency, tag);
  const gross = (m: number) => (vat === null ? null : estimateGross(m, vat));
  const subGross = subNet === null ? null : gross(subNet);
  const minGross = minNet === null ? null : gross(minNet);
  // The package rides on the subscription's first invoice: ONE payment today.
  const withPackage = !!pkg && minNet !== null;
  const total = subGross !== null ? subGross + (withPackage && minGross !== null ? minGross : 0) : null;
  const pay = () => checkout.mutate({ packageId: withPackage ? pkg!.packageId : null });
  const priced = subNet !== null;

  const cta = (
    <Button
      block
      size="lg"
      loading={checkout.isPending}
      // One payment at a time: a second click while Stripe is being asked for
      // a session would open two subscriptions.
      disabled={!priced || checkout.isPending}
      onClick={pay}
    >
      {t('plan.cta.subscribe')}
    </Button>
  );

  return (
    <div className="plan-grid">
      <div className="plan-main">
        <SubscriptionBlock ov={ov} />
        <section>
          <h2 id="plan-pk-h" className="plan-h2">
            {optional ? t('plan.packages.optionalTitle') : t('plan.packages.title')}
            {optional && <span className="plan-h2__tag"> · {t('plan.packages.optionalTag')}</span>}
          </h2>
          <p className="plan-muted plan-section-hint">{optional ? t('plan.packages.optionalSub') : t('plan.packages.hint')}</p>
          <PackagePicker
            packages={ov.packages}
            selected={selected}
            onSelect={choose}
            labelledBy="plan-pk-h"
            none={optional ? { label: t('plan.package.none'), hint: t('plan.package.noneHint', { minutes: includedMinutes }) } : undefined}
          />
          <CallsFootnote />
        </section>
      </div>

      <aside id="plan-summary" className="plan-aside" aria-labelledby="plan-sum-h">
        <h2 id="plan-sum-h" className="plan-aside__title">{t('plan.summary.onePayment')}</h2>
        {subNet === null && <Banner tone="warning">{t('plan.priceUnavailable')}</Banner>}
        {subNet !== null && (
          <Line
            label={
              optional ? (
                <LineLabel main={t('plan.summary.subscriptionPlain')} sub={t('plan.summary.subIncludedSub', { minutes: includedMinutes })} />
              ) : (
                t('plan.summary.subscriptionPlain')
              )
            }
            value={
              <>
                {money(subGross ?? subNet)}
                <span className="plan-line__per">{t('plan.summary.perMonthTiny')}</span>
              </>
            }
          />
        )}
        {pkg && minNet !== null && (
          <Line
            label={<LineLabel main={t('plan.summary.extraMinutes', { minutes: packageMinutes(pkg) })} sub={t('plan.summary.extraTogether')} />}
            value={money(minGross ?? minNet)}
          />
        )}
        {total !== null && (
          <Line strong label={t('plan.summary.dueToday')} value={money(total)} />
        )}
        {vat !== null && subNet !== null ? (
          <details className="plan-breakdown">
            <summary>{t('plan.summary.vatBreakdown', { vat })}</summary>
            <Line label={t('plan.summary.subNet')} value={money(subNet)} />
            {withPackage && <Line label={t('plan.summary.minNet')} value={money(minNet!)} />}
            <Line label={t('plan.summary.vat', { vat })} value={money((total ?? 0) - subNet - (withPackage ? minNet! : 0))} />
            <p className="plan-muted plan-small">{t('plan.summary.estimate', { vat })}</p>
          </details>
        ) : (
          <p className="plan-muted plan-small">{t('plan.summary.noEstimate')}</p>
        )}
        <div className="plan-cta">
          {cta}
          <p className="plan-muted plan-small plan-center">
            {withPackage
              ? t('plan.cta.togetherHint', { amount: money(subGross ?? subNet!) })
              : optional
                ? t('plan.cta.renewsHint', { minutes: includedMinutes })
                : t('plan.cta.subscribeOnlyHint')}
          </p>
        </div>
        {checkout.isError && <Banner tone="danger" title={t('plan.failed')}>{friendlyError(t, checkout.error)}</Banner>}
        <div aria-live="polite" className="visually-hidden">
          {subNet !== null
            ? pkg
              ? t('plan.summary.liveTogether', { minutes: packageMinutes(pkg), amount: money(total ?? subNet) })
              : t('plan.summary.liveNone', { amount: money(subGross ?? subNet) })
            : ''}
        </div>
      </aside>

      {subNet !== null && (
        <div className="plan-sticky">
          <a href="#plan-summary" className="plan-sticky__sum">
            <span className="plan-small plan-muted">{withPackage ? t('plan.sticky.todayVat') : t('plan.sticky.nextVat')}</span>
            <span className="plan-sticky__amount">
              {withPackage ? money(total ?? subNet) : `${money(subGross ?? subNet)}${t('plan.summary.perMonthTiny')}`}
            </span>
          </a>
          <Button loading={checkout.isPending} disabled={checkout.isPending} onClick={pay}>
            {t('plan.cta.subscribe')}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Subscribed: buy one package ─────────────────────────────────────────────

/** Debounce a value so arrowing through the radios does not quote each one. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export function BuyMinutes({
  business,
  ov,
  sticky,
  title,
  optional,
  onSkip,
}: {
  business: BusinessDoc;
  ov: Overview;
  sticky?: boolean;
  title?: string;
  /** Right after activation (design v4): the included minutes already work,
   *  so the purchase is presented as optional. */
  optional?: boolean;
  /** "Skip for now" under the button — the way past an optional purchase. */
  onSkip?: () => void;
}) {
  const { t, tag } = useI18n();
  const { toast } = useToast();
  const [selected, choose] = usePackageSelection(business.id, ov.packages);
  const pkg = ov.packages.find((p) => p.packageId === selected) ?? null;
  const blocked = ov.entitlement.kind === 'blocked';
  const order = ov.activeOrder;
  const quoted = useDebounced(selected, 350);
  const quote = usePackageQuote(business.id, quoted, !blocked && !order);
  const buy = useBuyTopup(business.id);
  const portal = useOpenBillingPortal(business.id);
  const money = (m: number) => formatMoney(m, ov.currency, tag);

  if (order) return <ActiveOrder business={business} ov={ov} order={order} />;
  if (ov.packages.length === 0) return <p className="plan-muted">{t('prepaid.topup.none')}</p>;

  // Only a quote for the package on screen counts: while the debounce or the
  // request is in flight, the button says so instead of showing another
  // package's price.
  const q = quote.data && quote.data.packageId === selected ? quote.data : null;
  const expired = q ? new Date(q.expiresAt).getTime() <= Date.now() : false;
  const ready = !!q && !expired && q.taxComplete && !blocked;
  const minutes = pkg ? packageMinutes(pkg) : 0;
  const pricing = !blocked && (quote.isFetching || quoted !== selected);
  // A quote left on screen past its life is refreshed by hand, not on a
  // timer: each refresh is a paid tax calculation.
  const stale = !!q && expired && !blocked && !pricing;
  // A plain "Buy": the total is in the summary right above it.
  const label = !pkg
    ? t('plan.package.unavailable')
    : ready
      ? t('plan.cta.buy')
      : pricing
        ? t('plan.cta.pricing')
        : stale
          ? t('prepaid.topup.quote')
          : t('plan.cta.buy');

  const onBuy = () => {
    if (!q) return;
    if (expired) {
      void quote.refetch();
      return;
    }
    // Straight on to Stripe's payment page, in a NEW tab: Stripe's invoice
    // page has no way back to us, so this tab stays put and turns into the
    // active-order card, which notices the payment when the owner returns.
    // The tab is opened now, inside the click — opened after the network
    // round trip, a popup blocker would eat it. Blocked anyway: the card's own
    // "Pay" link is the way on.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    buy.mutate(q.quoteId, {
      onSuccess: (order) => {
        if (tab && order.hostedInvoiceUrl) tab.location.href = order.hostedInvoiceUrl;
        else tab?.close();
      },
      onError: (e) => {
        tab?.close();
        // A lapsed or repriced quote: fetch the new price and let the owner
        // look at it before paying.
        const code = billingErrorDetails(e).code;
        if (code === 'quote_expired' || code === 'price_changed') void quote.refetch();
        toast({ title: billingError(t, e), tone: 'danger' });
      },
    });
  };

  const button = (
    <Button block size="lg" disabled={!(ready || stale) || buy.isPending} loading={buy.isPending} onClick={onBuy}>
      {label}
    </Button>
  );

  return (
    <div className="plan-grid">
      <div className="plan-main">
        <section>
          <h2 id="plan-buy-h" className="plan-h2">
            {title ?? (optional ? t('plan.packages.optionalTitle') : t('plan.packages.buyTitle'))}
            {optional && !title && <span className="plan-h2__tag"> · {t('plan.packages.optionalTag')}</span>}
          </h2>
          <p className="plan-muted plan-section-hint">{t('plan.packages.buyHint')}</p>
          <PackagePicker packages={ov.packages} selected={selected} onSelect={choose} labelledBy="plan-buy-h" plus />
          <CallsFootnote />
        </section>
      </div>

      <aside id="plan-summary" className="plan-aside" aria-labelledby="plan-pay-h">
        <h2 id="plan-pay-h" className="plan-aside__title">{t('plan.summary.payNow')}</h2>
        {/* Net, VAT and total in the open, right above the button: the owner
            sees exactly how much of the price is tax before they pay, not
            behind a "show breakdown" disclosure. The rate is the server's
            quote for this customer, never an estimate. While that quote is
            on its way, a skeleton — never the list price as a stand-in. */}
        {pkg && !q && pricing && <QuoteSkeleton />}
        {pkg && !(!q && pricing) && (
          <Line
            strong={!q}
            label={<LineLabel main={t('plan.summary.extraMinutes', { minutes })} sub={t('plan.summary.oneTime')} />}
            value={q ? money(q.netMinor) : pkg.unitAmountMinor !== null ? `${money(pkg.unitAmountMinor)} ${t('plan.package.plusVat')}` : '—'}
          />
        )}
        {q && (
          <>
            <Line
              label={q.netMinor > 0 ? t('plan.summary.vatQuoted', { vat: Math.round((q.taxMinor / q.netMinor) * 100) }) : t('plan.summary.tax')}
              value={money(q.taxMinor)}
            />
            <Line strong label={t('plan.summary.total')} value={money(q.grossMinor)} />
          </>
        )}
        {blocked && <Banner tone="warning">{t('plan.notEntitled')}</Banner>}
        {q && !q.taxComplete && (
          <Banner
            tone="warning"
            title={t('prepaid.error.tax_location_required')}
            actions={
              <Button size="sm" variant="secondary" loading={portal.isPending} onClick={() => portal.mutate()}>
                {t('plan.updateBilling')}
              </Button>
            }
          >
            {t('plan.taxMissing')}
          </Banner>
        )}
        {quote.isError && (
          <Banner
            tone="danger"
            actions={<Button size="sm" variant="secondary" onClick={() => void quote.refetch()}>{t('prepaid.retry')}</Button>}
          >
            {billingError(t, quote.error)}
          </Banner>
        )}
        <div className="plan-cta">
          {button}
          <p className="plan-muted plan-small plan-center">{optional ? t('plan.cta.optionalHint') : t('plan.cta.noRecharge')}</p>
          {onSkip && (
            <Button block variant="ghost" className="plan-skip" onClick={onSkip}>
              {t('plan.cta.skip')}
            </Button>
          )}
        </div>
        <p className="plan-muted plan-small">{t('plan.cta.buyHint')}</p>
        <div aria-live="polite" className="visually-hidden">{ready ? label : ''}</div>
      </aside>

      {sticky && (
        <div className="plan-sticky">
          <a href="#plan-summary" className="plan-sticky__sum">
            <span className="plan-small plan-muted">{t('plan.summary.payNow')}</span>
            <span className="plan-sticky__amount">{ready ? money(q!.grossMinor) : '—'}</span>
          </a>
          <Button disabled={!(ready || stale) || buy.isPending} loading={buy.isPending} onClick={onBuy}>
            {t('plan.cta.buy')}
          </Button>
        </div>
      )}
    </div>
  );
}

/** A payment already in flight: pay it, check it, or cancel it — never a second one. */
function ActiveOrder({ business, ov, order }: { business: BusinessDoc; ov: Overview; order: NonNullable<Overview['activeOrder']> }) {
  const { t, tag } = useI18n();
  const { toast } = useToast();
  const sync = useSyncTopup(business.id);
  const cancel = useCancelTopup(business.id);
  return (
    <div className="plan-order" role="status">
      <h2 className="plan-h2">{t('plan.order.title')}</h2>
      <p>
        {order.purpose === 'auto' ? t('prepaid.topup.activeAuto') : t('prepaid.topup.activeManual')}{' '}
        <strong>{formatMoney(order.grossMinor, ov.currency, tag)}</strong>
      </p>
      {order.expiresAt && (
        <p className="plan-muted plan-small">
          {t('prepaid.topup.validUntil', { time: new Intl.DateTimeFormat(tag, { timeStyle: 'short' }).format(new Date(order.expiresAt)) })}
        </p>
      )}
      <p className="plan-muted plan-small">{t('prepaid.topup.waiting')}</p>
      <div className="plan-actions">
        {order.hostedInvoiceUrl && (
          <a className="tc-btn tc-btn--primary tc-btn--md" href={order.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
            {t('prepaid.topup.pay')}
          </a>
        )}
        <Button
          variant="secondary"
          loading={sync.isPending}
          onClick={() =>
            sync.mutate(order.orderId, {
              onSuccess: (r) =>
                toast({ title: r.status === 'fulfilled' ? t('prepaid.topup.credited') : t('prepaid.topup.notYet'), tone: r.status === 'fulfilled' ? 'success' : 'info' }),
              onError: (e) => toast({ title: billingError(t, e), tone: 'danger' }),
            })
          }
        >
          {t('prepaid.topup.check')}
        </Button>
        <Button
          variant="ghost"
          loading={cancel.isPending}
          onClick={() => cancel.mutate(order.orderId, { onError: (e) => toast({ title: billingError(t, e), tone: 'danger' }) })}
        >
          {t('prepaid.topup.cancel')}
        </Button>
      </div>
    </div>
  );
}
